import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import styled from '@emotion/styled';
import InfoTip from './InfoTip';
import TreatmentTrends from './TreatmentTrends';

const COLORS = ['#1976D2', '#00B8D4', '#5C6BC0', '#8BC34A', '#880E4F', '#006A71', '#1E2D3D', '#E0E4E8'];
const ELIGIBILITY_COLORS = ['#4CB847', '#E12E40'];

function formatPct(n: unknown): string {
  return n != null ? `${(Number(n) * 100).toFixed(1)}%` : '—';
}

/* ── KPI Card ─────────────────────────────────────────────────────────── */

const KPIBox = styled.div`
  background: #ffffff;
  border: 1px solid #e5e5e5;
  border-top: 3px solid #28aae1;
  border-radius: 8px;
  padding: 14px;
  text-align: center;
  position: relative;
  transition: all 0.2s;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
  &:hover > div:last-of-type {
    opacity: 1;
  }
`;

const KPIValue = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #323232;
`;

const KPILabel = styled.div`
  font-size: 11px;
  color: #adadad;
  margin-top: 4px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const KPITooltip = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  width: 220px;
  background: #323232;
  color: #ffffff;
  font-size: 11px;
  line-height: 1.5;
  border-radius: 8px;
  padding: 10px 12px;
  opacity: 0;
  pointer-events: none;
  transition: opacity 80ms;
  z-index: 50;
  white-space: normal;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
`;

interface KPICardProps {
  label: string;
  value: string;
  tooltip?: string;
}

function KPICard({ label, value, tooltip }: KPICardProps): React.JSX.Element {
  return (
    <KPIBox>
      <KPIValue>{value}</KPIValue>
      <KPILabel>{label}</KPILabel>
      {tooltip && <KPITooltip>{tooltip}</KPITooltip>}
    </KPIBox>
  );
}

/* ── HCP Characteristics ──────────────────────────────────────────────── */

const ChartBox = styled.div`
  background: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 16px;
`;

const tooltipStyle = {
  background: '#ffffff',
  border: '1px solid #e5e5e5',
  fontSize: 11,
  borderRadius: 6,
};

interface HCPCharacteristicsProps {
  data:
    | {
        hcp_volume_distribution?: { range: string; count: number }[];
        age_distribution?: { range: string; count: number }[];
        transplant_eligibility?: { status: string; count: number; percentage: number }[];
      }
    | undefined;
}

function HCPCharacteristics({ data }: HCPCharacteristicsProps): React.JSX.Element | null {
  if (!data) return null;
  const vol = data.hcp_volume_distribution ?? [];
  const age = data.age_distribution ?? [];
  const elig = data.transplant_eligibility ?? [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <h2 style={{ fontSize: 14, fontWeight: 600, color: '#323232', margin: 0 }}>HCP Characteristics</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
        <ChartBox>
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: '#323232',
              marginBottom: 4,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            HCP Distribution by Patient Volume
            <InfoTip text="Each HCP's total unique patient count is bucketed into volume bands. The volume filter determines which HCPs enter segmentation." />
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={vol} margin={{ top: 5, right: 10, left: -15, bottom: 5 }}>
              <XAxis dataKey="range" tick={{ fill: '#626262', fontSize: 10 }} />
              <YAxis tick={{ fill: '#626262', fontSize: 10 }} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="count" name="HCPs" fill="#28AAE1" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartBox>

        <ChartBox>
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: '#323232',
              marginBottom: 4,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            Age Distribution of Patients
            <InfoTip text="Patient age at diagnosis, grouped into decade bins. Outlier ages (<10 or >100) are dropped as likely data errors." />
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={age} margin={{ top: 5, right: 10, left: -15, bottom: 5 }}>
              <XAxis dataKey="range" tick={{ fill: '#626262', fontSize: 10 }} />
              <YAxis tick={{ fill: '#626262', fontSize: 10 }} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="count" name="Patients" fill="#4CB847" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartBox>

        <ChartBox>
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: '#323232',
              marginBottom: 4,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            Transplant Eligibility
            <InfoTip text="Each patient is flagged TIE (Transplant Ineligible) or eligible from the raw flag. Used as a severity / disease-stage proxy." />
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: 240,
            }}
          >
            <PieChart width={220} height={180}>
              <Pie
                data={elig}
                cx={110}
                cy={85}
                outerRadius={70}
                dataKey="count"
                nameKey="status"
                isAnimationActive={false}
                label={({ percent }: { percent: number }) => `${(percent * 100).toFixed(0)}%`}
                labelLine={false}
              >
                {elig.map((_, i) => (
                  <Cell key={i} fill={ELIGIBILITY_COLORS[i]} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(v: number, n: string) => [`${v} patients`, n]}
              />
            </PieChart>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 }}>
              {elig.map((d, i) => (
                <div key={d.status} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <div
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: ELIGIBILITY_COLORS[i],
                    }}
                  />
                  <span style={{ fontSize: 11, color: '#323232' }}>
                    {d.status} ({d.percentage}%)
                  </span>
                </div>
              ))}
            </div>
          </div>
        </ChartBox>
      </div>
    </div>
  );
}

/* ── MarketView ───────────────────────────────────────────────────────── */

export interface MarketViewProps {
  marketKpis?: {
    pct_of_hcp?: number | null;
    pct_of_patient_population?: number | null;
    avg_patient_age?: number | null;
    avg_new_starts_per_month?: number | null;
    pct_transplant_ineligible?: number | null;
    line1_concentration?: number | null;
  };
  hcpCharacteristics?: HCPCharacteristicsProps['data'];
  treatmentTrends?: Record<string, unknown>;
}

export default function MarketView({
  marketKpis,
  hcpCharacteristics,
  treatmentTrends,
}: MarketViewProps): React.JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {marketKpis && (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 10 }}>
        <KPICard
          label="% of HCP"
          value={formatPct(marketKpis?.pct_of_hcp)}
          tooltip="Share of HCPs retained after the volume filter. These are the HCPs that will be segmented."
        />
        <KPICard
          label="% of Patient Pop."
          value={formatPct(marketKpis?.pct_of_patient_population)}
          tooltip="Share of total patients covered by the retained HCPs."
        />
        <KPICard
          label="Avg Patient Age"
          value={marketKpis?.avg_patient_age != null ? String(marketKpis.avg_patient_age) : '—'}
          tooltip="Mean age at diagnosis across all patients."
        />
        <KPICard
          label="Avg New Starts/Mo"
          value={formatPct(marketKpis?.avg_new_starts_per_month)}
          tooltip="Per HCP, the average monthly share of patients starting a new line of therapy."
        />
        <KPICard
          label="% Transplant Inelig."
          value={formatPct(marketKpis?.pct_transplant_ineligible)}
          tooltip="Share of patients flagged Transplant Ineligible."
        />
        <KPICard
          label="L1 Concentration"
          value={formatPct(marketKpis?.line1_concentration)}
          tooltip="Per HCP, the average monthly share of active patients on Line 1."
        />
      </div>
      )}
      <HCPCharacteristics data={hcpCharacteristics} />
      <TreatmentTrends data={treatmentTrends} />
    </div>
  );
}

export { KPICard };
